import { buildHotelWebsiteLabel } from 'features/EmailEditor/utils'
import { formatPhoneNumber } from 'utils/helpers'

function getCountryName(countryCode) {
  if (!countryCode) return null

  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode.toUpperCase())
  } catch {
    return countryCode
  }
}

function buildHotelWebsiteUrl(primaryWebsiteUrl) {
  if (!primaryWebsiteUrl) return null

  const trimmed = primaryWebsiteUrl.trim()
  return trimmed.startsWith('http://') || trimmed.startsWith('https://')
    ? trimmed
    : `https://${trimmed}`
}

function buildHotelWebsiteDisplay(primaryWebsiteUrl) {
  return buildHotelWebsiteLabel(primaryWebsiteUrl) || null
}

function buildFullHotelAddress(subsidiary) {
  const parts = [
    subsidiary?.streetAddress,
    subsidiary?.city,
    subsidiary?.stateCode,
    subsidiary?.postalCode,
    getCountryName(subsidiary?.countryCode)
  ].filter(Boolean)

  return parts.length ? parts.join(', ') : null
}

export function buildHotelIdentityParams(subsidiary) {
  if (!subsidiary) return {}

  const params = {}
  const fullAddress = buildFullHotelAddress(subsidiary)

  if (subsidiary.reservationHotLine) {
    params.hotelPhone = formatPhoneNumber(subsidiary.reservationHotLine)
  }

  if (subsidiary.primaryWebsiteUrl) {
    params.hotelWebsiteUrl = buildHotelWebsiteUrl(subsidiary.primaryWebsiteUrl)
    params.hotelWebsite = buildHotelWebsiteDisplay(subsidiary.primaryWebsiteUrl)
  }

  if (subsidiary.hotelName) {
    params.hotelName = subsidiary.hotelName
  }

  if (fullAddress) {
    params.hotelAddress = fullAddress
    params.hotelCityStateZip = ''
  } else if (subsidiary.city || subsidiary.stateCode || subsidiary.postalCode) {
    params.hotelCityStateZip = [
      subsidiary.city,
      [subsidiary.stateCode, subsidiary.postalCode].filter(Boolean).join(' ')
    ]
      .filter(Boolean)
      .join(', ')
  }

  return params
}

export function mergeHotelIdentityParams(emailState, hotelIdentityParams) {
  return {
    ...emailState,
    ...hotelIdentityParams
  }
}

function hasIdentityValue(value) {
  return `${value ?? ''}`.trim().length > 0
}

// Template hydration can arrive after the hotel details and reset identity fields to the
// shell defaults, so missing values are refilled while anything already on screen (a saved
// label or an in-progress edit) is left untouched.
export function fillMissingHotelIdentityParams(emailState, hotelIdentityParams) {
  const next = { ...emailState }

  Object.entries(hotelIdentityParams || {}).forEach(([field, value]) => {
    if (!hasIdentityValue(next[field])) {
      next[field] = value
    }
  })

  return next
}

// Only a label the user actually wrote is worth storing; a copy of the derived host would
// go stale as soon as the hotel changes its website.
export function resolveCustomHotelWebsiteLabel(websiteLabel, derivedWebsiteLabel) {
  const label = `${websiteLabel ?? ''}`.trim()

  if (!label || label === `${derivedWebsiteLabel ?? ''}`.trim()) {
    return ''
  }

  return label
}
